import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import {
  persistQueryClientRestore,
  persistQueryClientSave,
  persistQueryClientSubscribe,
} from '@tanstack/query-persist-client-core'
import { createStore, del, get, set } from 'idb-keyval'
import { queryClient } from '@/lib/queryClient'

// The query cache (including paused offline mutations) is persisted to
// IndexedDB so it survives restarts. Each tenant subdomain is its own origin,
// so caches are tenant-scoped for free.
const idbStore = createStore('flowpilot-offline', 'query-cache')
const PERSIST_KEY = 'flowpilot:query-client'
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7 // one week, matching queryClient gcTime

// Deploy marker: a client running an older build discards its persisted cache
// on restore instead of hydrating a stale shape over new code.
const BUSTER = process.env.BUILD_ID || 'dev'

const persister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => (await get<string>(key, idbStore)) ?? null,
    setItem: async (key, value) => {
      await set(key, value, idbStore)
    },
    removeItem: async (key) => {
      await del(key, idbStore)
    },
  },
  key: PERSIST_KEY,
  throttleTime: 3_000,
})

// Hydrate the cache before first render so an offline boot starts with data.
// A storage failure (private browsing, corrupted entry) must never block
// startup — drop the cache and continue empty.
export async function restorePersistedCache(): Promise<void> {
  try {
    await persistQueryClientRestore({
      queryClient,
      persister,
      maxAge: MAX_AGE_MS,
      buster: BUSTER,
    })
  } catch {
    try {
      await persister.removeClient()
    } catch {
      // nothing more we can do
    }
  }
}

// Keep the persisted copy in sync (throttled by the persister) and force a
// flush when the page goes away so the last state — including mutations
// paused moments before the unload — is not lost.
export function subscribePersistedCache(): () => void {
  const unsubscribe = persistQueryClientSubscribe({ queryClient, persister, buster: BUSTER })
  const flush = () => {
    void persistQueryClientSave({ queryClient, persister, buster: BUSTER })
  }
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush()
  }
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', onVisibility)
  return () => {
    unsubscribe()
    window.removeEventListener('pagehide', flush)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}

// Called on logout and login (features/auth/hooks.ts): the next session must
// not see the previous user's data. Clears both the in-memory cache and the
// persisted copy. Callers that need to seed the fresh session's /auth/me
// should do so after this resolves.
export async function clearPersistedCache(): Promise<void> {
  queryClient.clear()
  try {
    await persister.removeClient()
  } catch {
    // ignore — a stale blob is dropped by the buster/maxAge check eventually
  }
}
